'use client';

import React from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import { ArrowUpRight } from 'lucide-react';
import { patchListingState } from '@/app/category-listing-page/lib/listingState';
import { formatPackagingContentCompact } from '@/lib/stock/formatStock';
import type { PackagingConfig } from '@/lib/api/services';

function persistBrowsePosition() {
  if (typeof window === 'undefined') return;
  patchListingState({ scrollY: window.scrollY });
}

export interface ProductCardData {
  id: string;
  title: string;
  code: string;
  sellingPrice: number;
  /** ₹ amount off selling price */
  discountPrice?: number;
  mainImage?: string;
  stockQuantity?: number;
  isFeatured?: boolean;
  isNewArrival?: boolean;
  packaging?: PackagingConfig | null;
}

interface ProductCardProps {
  prod: ProductCardData;
}

export default function ProductCard({ prod }: ProductCardProps) {
  const isOutOfStock = prod.stockQuantity === 0;
  const isComingSoon = Boolean(prod.isFeatured);
  const sellingPrice = Number(prod.sellingPrice) || 0;
  const discountAmount = Number(prod.discountPrice) || 0;
  const hasDiscount = discountAmount > 0 && discountAmount < sellingPrice;
  const offerPrice = hasDiscount ? sellingPrice - discountAmount : sellingPrice;
  const packingLabel = formatPackagingContentCompact(prod.packaging);

  return (
    <article className="group bg-white">
      <Link href={`/product-detail-page?id=${prod.id}`} onClick={persistBrowsePosition}>
        <div className="relative overflow-hidden rounded-lg bg-zinc-100">
          <AppImage
            src={prod.mainImage}
            alt={prod.title}
            width={400}
            height={400}
            className="aspect-square w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />

          {isComingSoon && (
            <span className="absolute right-3 top-3 rounded-full bg-zinc-950 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-white">
              Coming Soon
            </span>
          )}

          {prod.isNewArrival && !isOutOfStock && !isComingSoon && (
            <span className="absolute right-3 top-3 rounded-full border border-zinc-950 bg-lime-200 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-zinc-950 shadow-sm">
              New
            </span>
          )}

          {isOutOfStock && !isComingSoon && (
            <span className="absolute right-3 top-3 rounded-full bg-zinc-950 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-white">
              Out of Stock
            </span>
          )}

          <span className="absolute bottom-3 right-3 rounded-full bg-white px-2.5 py-1 text-xs font-medium text-zinc-950 shadow-sm">
            {prod.code}
          </span>
        </div>
      </Link>

      <div className="pt-3">
        <Link href={`/product-detail-page?id=${prod.id}`} onClick={persistBrowsePosition}>
          <h3 className="line-clamp-1 min-h-[20px] text-[15px] font-medium leading-tight text-zinc-950 transition-colors group-hover:text-zinc-700 sm:line-clamp-2 sm:min-h-[38px] sm:text-base">
            {prod.title}
          </h3>
        </Link>
        {packingLabel && <p className="mt-1 text-xs text-zinc-500">{packingLabel}</p>}

        <div className="mt-3 flex items-center justify-between gap-3">
          <div className="flex min-w-0 flex-wrap items-baseline gap-1.5">
            <p className="rounded-full bg-lime-200 px-3 py-1 text-sm font-bold text-zinc-950 ring-1 ring-lime-300">
              ₹{offerPrice}
            </p>
            {hasDiscount && (
              <span className="text-xs text-zinc-400 line-through tabular-nums">
                ₹{sellingPrice}
              </span>
            )}
          </div>
          <Link
            href={`/product-detail-page?id=${prod.id}`}
            onClick={persistBrowsePosition}
            className="inline-flex items-center gap-1 text-sm font-medium text-zinc-950 transition-colors hover:text-zinc-600"
          >
            Inquire now
            <ArrowUpRight size={15} />
          </Link>
        </div>
      </div>
    </article>
  );
}
